import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SystemStatusProvider, useSystemStatus } from './context/SystemStatusContext';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import InterviewPage from './pages/InterviewPage';
import InterviewReportPage from './pages/InterviewReportPage';
import NotificationsPage from './pages/NotificationsPage';
import ClassicQuestionsPage from './pages/ClassicQuestionsPage';
import TicketList from './pages/TicketList';
import TicketCreate from './pages/TicketCreate';
import TicketDetail from './pages/TicketDetail';
import AdminLogin from './pages/AdminLogin';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import WorkplaceNewsDetailPage from './pages/WorkplaceNewsDetailPage.jsx';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Components
import Navbar from './components/common/Navbar';
import UnifiedLoginModal from './components/common/UnifiedLoginModal';
import AlertModal from './components/common/AlertModal';
import ServiceUnavailable from './components/common/ServiceUnavailable';
import JobDetail from './components/dashboard/JobDetail';
import QuestionDetail from './components/dashboard/QuestionDetail';
import AdminLayout from './components/admin/AdminLayout.jsx';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.5;
  }

  html {
    scroll-behavior: smooth;
  }

  button {
    font-family: inherit;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  /* Loading animation */
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 18px;
`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <LoadingContainer>
        <div>加载中...</div>
      </LoadingContainer>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/" />;
};

// Admin Protected Route Component - 管理员路由保护
const AdminProtectedRoute = ({ children }) => {
  const [isVerifying, setIsVerifying] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const navigate = useNavigate();
  
  React.useEffect(() => {
    const verifyAdmin = async () => {
      const token = localStorage.getItem('admin_token');
      const adminUser = localStorage.getItem('admin_user');
      
      if (!token || !adminUser) {
        setIsVerifying(false);
        setIsAdmin(false);
        return;
      }
      
      try {
        const userData = JSON.parse(adminUser);
        if (userData && userData.role && (userData.role === 'operation_admin' || userData.role === 'support_admin')) {
          setIsAdmin(true);
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          setIsAdmin(false);
        }
      }
      catch (error) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        setIsAdmin(false);
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyAdmin();
  }, []);
  
  if (isVerifying) {
    return (
      <LoadingContainer>
        <div>验证管理员身份...</div>
      </LoadingContainer>
    );
  }
  
  if (!isAdmin) {
    // 跳转到管理员登录页
    return <Navigate to="/sys-portal/login" replace />;
  }
  
  return children;
};

const InviteRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
};

function AppContent() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [authErrorModal, setAuthErrorModal] = useState({ isOpen: false, message: '' });
  const { isAuthenticated } = useAuth();
  const { isHealthy, status } = useSystemStatus();
  const location = useLocation();

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleCloseAuthErrorModal = () => {
    setAuthErrorModal({ isOpen: false, message: '' });
    // 关闭错误提示后，打开登录弹窗
    if (window.location.pathname !== '/sys-portal/login') {
      setIsLoginModalOpen(true);
    }
  };

  // 监听认证错误事件，显示错误提示并打开登录弹窗
  useEffect(() => {
    const handleAuthError = (event) => {
      const message = event.detail?.message || '登录已过期，请重新登录';
      // 防止重复显示弹框
      setAuthErrorModal((prev) => {
        // 如果弹框已经打开，不再重复打开
        if (prev.isOpen) {
          return prev;
        }
        return { isOpen: true, message };
      });
    };

    window.addEventListener('authError', handleAuthError);

    return () => {
      window.removeEventListener('authError', handleAuthError);
    };
  }, []);

  // 当用户登录成功后，自动跳转到仪表盘
  useEffect(() => {
    if (isAuthenticated && window.location.pathname === '/') {
      // 使用 setTimeout 确保状态更新完成后再跳转
      setTimeout(() => {
        window.location.href = '/app';
      }, 100);
    }
  }, [isAuthenticated]);

  // 监听用户活动：
  // 1）在用户最近有操作且 access_token 即将过期时，尝试静默刷新；
  // 2）当连续 30 分钟没有任何鼠标/键盘/滚动等操作时，自动退出登录并回到首页。
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let lastActivityTime = Date.now();
    let checkInterval = null;
    let activityTimeout = null;
    let idleCheckInterval = null;
    let isIdleLogoutTriggered = false;

    // 检查token是否即将过期（还剩5分钟时刷新）
    const checkAndRefreshToken = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          return;
        }

        // 解析JWT token获取过期时间
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const exp = payload.exp * 1000; // 转换为毫秒
          const now = Date.now();
          const timeUntilExpiry = exp - now;
          
          // 如果token还剩不到1分钟就过期，且用户最近5分钟内有活动，则尝试刷新token
          // 刷新失败（例如 refresh_token 缺失或过期）时，直接跳转到登录主页
          const timeSinceLastActivity = Date.now() - lastActivityTime;
          if (timeUntilExpiry < 1 * 60 * 1000 && timeSinceLastActivity < 5 * 60 * 1000) {
            const { tryRefreshToken, handleAuthError } = await import('./utils/authErrorHandler');
            const refreshSuccess = await tryRefreshToken();

            if (!refreshSuccess) {
              await handleAuthError('登录已过期，请重新登录', false, true);
            }
          }
        } catch (error) {
        }
      } catch (error) {
      }
    };

    // 用户活动处理函数
    const handleUserActivity = () => {
      // 记录最近一次用户操作时间
      lastActivityTime = Date.now();
      
      // 清除之前的定时器
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      
      // 延迟检查（防抖，避免频繁检查）
      activityTimeout = setTimeout(() => {
        checkAndRefreshToken();
      }, 1000);
    };

    // 监听用户活动事件
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // 定期检查token（每5分钟检查一次）
    checkInterval = setInterval(() => {
      checkAndRefreshToken();
    }, 5 * 60 * 1000);

    // 定期检查是否长时间无操作（每分钟检查一次）
    idleCheckInterval = setInterval(async () => {
      if (isIdleLogoutTriggered) {
        return;
      }
      const now = Date.now();
      const idleMs = now - lastActivityTime;
      const MAX_IDLE_MS = 30 * 60 * 1000; // 30 分钟

      if (idleMs >= MAX_IDLE_MS) {
        isIdleLogoutTriggered = true;
        try {
          const { handleAuthError } = await import('./utils/authErrorHandler');
          await handleAuthError('长时间未操作，为保障账号安全已自动退出登录，请重新登录', false, true);
        } catch (error) {
        }
      }
    }, 60 * 1000);

    // 初始检查
    checkAndRefreshToken();

    return () => {
      // 清理事件监听器
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      
      // 清理定时器
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      if (idleCheckInterval) {
        clearInterval(idleCheckInterval);
      }
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
    };
  }, [isAuthenticated]);

  // 如果系统不可用，显示服务不可用页面
  if (!isHealthy && status === 'unhealthy') {
    return <ServiceUnavailable />;
  }

  return (
    <AppContainer>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <HomePage onLoginClick={handleLoginClick} />
          </>
        } />
        
        <Route path="/invite" element={<InviteRedirect />} />

        {/* Classic Questions Public Route */}
        <Route path="/classic-questions" element={
          <>
            <ClassicQuestionsPage />
            <UnifiedLoginModal 
              isOpen={isLoginModalOpen}
              onClose={handleCloseModal}
            />
          </>
        } />

        {/* Workplace News Detail Public Route */}
        <Route path="/workplace-news/:id" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <WorkplaceNewsDetailPage />
            <UnifiedLoginModal 
              isOpen={isLoginModalOpen}
              onClose={handleCloseModal}
            />
          </>
        } />

        {/* Privacy and Terms Public Routes */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        
        {/* Reset Password Public Route */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected Routes */}
        {/* Unified workspace root (/app) with resource-oriented paths */}
        <Route path="/app" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />

        {/* Jobs */}
        {/* <Route path="/app/jobs/search" element={
          <ProtectedRoute>
            <DashboardPage activeView="job-assistant" />
          </ProtectedRoute>
        } /> */}
        <Route path="/app/jobs/:jobId" element={
          <ProtectedRoute>
            <JobDetail />
          </ProtectedRoute>
        } />

        {/* Resumes */}
        <Route path="/app/resumes/diagnosis" element={
          <ProtectedRoute>
            <DashboardPage activeView="resume-diagnosis" />
          </ProtectedRoute>
        } />

        {/* Interviews */}
        <Route path="/app/interviews/prep" element={
          <ProtectedRoute>
            <DashboardPage activeView="interview-prep" />
          </ProtectedRoute>
        } />
        <Route path="/app/interviews/mock" element={
          <ProtectedRoute>
            <DashboardPage activeView="mock-interview" />
          </ProtectedRoute>
        } />
        <Route path="/app/interviews/session" element={
          <ProtectedRoute>
            <InterviewPage />
          </ProtectedRoute>
        } />
        <Route path="/app/interviews/report/:interviewId" element={
          <ProtectedRoute>
            <InterviewReportPage />
          </ProtectedRoute>
        } />

        {/* Question details */}
        <Route path="/app/questions/:id" element={
          <ProtectedRoute>
            <QuestionDetail />
          </ProtectedRoute>
        } />

        {/* Meetings */}
        <Route path="/app/meetings" element={
          <ProtectedRoute>
            <DashboardPage activeView="meeting-assistant" />
          </ProtectedRoute>
        } />
        <Route path="/app/meetings/session" element={
          <ProtectedRoute>
            <DashboardPage activeView="meeting-assistant" />
          </ProtectedRoute>
        } />

        {/* Pricing */}
        <Route path="/app/pricing" element={
          <ProtectedRoute>
            <DashboardPage activeView="pricing" />
          </ProtectedRoute>
        } />

        {/* Notifications */}
        <Route path="/app/notifications" element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } />

        {/* Settings */}
        <Route path="/app/settings" element={
          <ProtectedRoute>
            <DashboardPage activeView="setting" />
          </ProtectedRoute>
        } />

        {/* Promotion & Support */}
        <Route path="/app/coop-promotion" element={
          <ProtectedRoute>
            <DashboardPage activeView="coop-promotion" />
          </ProtectedRoute>
        } />
        <Route path="/app/ticket-support" element={
          <ProtectedRoute>
            <DashboardPage activeView="ticket-support" />
          </ProtectedRoute>
        } />

        {/* Tickets as dedicated resource */}
        <Route path="/app/tickets" element={
          <ProtectedRoute>
            <TicketList />
          </ProtectedRoute>
        } />
        <Route path="/app/tickets/create" element={
          <ProtectedRoute>
            <TicketCreate />
          </ProtectedRoute>
        } />
        <Route path="/app/tickets/:id" element={
          <ProtectedRoute>
            <TicketDetail />
          </ProtectedRoute>
        } />

        

        {/* Admin Routes - Obfuscated Path with Protection */}
        <Route path="/sys-portal/login" element={<AdminLogin />} />
        <Route path="/sys-portal" element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        } />
        <Route path="/sys-portal/*" element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        } />

        {/* Legacy routes with navbar */}
        <Route path="/chat" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '80px', padding: '2rem', textAlign: 'center' }}>
              <h1>Search</h1>
              <p>请登录后使用完整功能</p>
            </div>
          </>
        } />
        
        <Route path="/resume" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />
        
        <Route path="/interview" element={<Navigate to="/classic-questions" replace />} />
        
        <Route path="/mock-interview" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        <Route path="/interview-prep" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        <Route path="/meeting-assistant" element={
          <>
            <Navbar onLoginClick={handleLoginClick} />
            <div style={{ paddingTop: '120px', padding: '2rem', minHeight: 'calc(100vh - 120px)', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <p style={{ fontSize: '1.1rem', color: '#64748b' }}>请登录后使用完整功能</p>
            </div>
          </>
        } />

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {/* Unified Login Modal */}
      <UnifiedLoginModal 
        isOpen={isLoginModalOpen}
        onClose={handleCloseModal}
      />

      {/* 认证错误提示弹窗 */}
      <AlertModal
        isOpen={authErrorModal.isOpen}
        onClose={handleCloseAuthErrorModal}
        title="登录已过期"
        message={authErrorModal.message}
        type="warning"
        confirmText="去登录"
        onConfirm={handleCloseAuthErrorModal}
      />
    </AppContainer>
  );
}

function App() {
  // 统一一次性清理/迁移旧本地存储键（只在应用挂载时执行一次）
  useEffect(() => {
    try {
      const migrate = (oldKey, newKey) => {
        const val = localStorage.getItem(oldKey);
        if (val && !localStorage.getItem(newKey)) {
          localStorage.setItem(newKey, val);
        }
        localStorage.removeItem(oldKey);
      };
      const migrateSS = (oldKey, newKey) => {
        const val = sessionStorage.getItem(oldKey);
        if (val && !sessionStorage.getItem(newKey)) {
          sessionStorage.setItem(newKey, val);
        }
        sessionStorage.removeItem(oldKey);
      };

      // 本地存储旧键 -> 新键
      migrate('jobAssistantJobResults', 'searchResults');
      migrate('jobAssistantSearchQuery', 'searchQuery');
      migrate('jobAssistantLastUserId', 'searchLastUserId');
      migrate('jobAssistantLastVisit', 'searchLastVisit');

      // 会话视图状态
      migrateSS('jobAssistantViewState', 'searchViewState');
    } catch {}
  }, []);

  return (
    <HelmetProvider>
      <SystemStatusProvider>
        <AuthProvider>
          <Router>
            <GlobalStyle />
            <AppContent />
          </Router>
        </AuthProvider>
      </SystemStatusProvider>
    </HelmetProvider>
  );
}

export default App;
